import { FC, useState } from "react";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import BookingDetailsSkeleton from "shared/Skelton/CommonSkelton";
// Existing, unchanged components are reused so behaviour stays identical.
import Invoice from "./components/InvoiceComposerModal"; // was: containers/Booking/Invoice (side drawer)
import InvoiceUser from "containers/Booking/InvoiceUser";
import ReviewForm from "containers/BookingRequest/Review";

import { useBookingRequestPage } from "./useBookingRequestPage";
import BookingHeader from "./components/BookingHeader";
import BookingStepper from "./components/BookingStepper";
import BookingTabs, { TabKey } from "./components/BookingTabs";
import NextStepCard from "./components/NextStepCard";
import StayDetails from "./components/StayDetails";
import PriceBreakdown from "./components/PriceBreakdown";
import ProfileCard from "./components/ProfileCard";
import PropertyCard from "./components/PropertyCard";
import PaymentSummary from "./components/PaymentSummary";
import ReviewPrompt from "./components/ReviewPrompt";
import InvoiceDetailsDrawer from "./components/InvoiceDetailsDrawer";
import MessageComposer from "./components/MessageComposer";
import MessageThread from "./components/MessageThread";
import InvoiceMessageCard from "./components/InvoiceMessageCard";

/**
 * Redesigned booking request page. Lives beside the old BookingRequest and is only
 * reachable through the new route / flag. Nothing in the old page is imported
 * from here, so rolling back = turning the flag off.
 */
const BookingRequestPage: FC = () => {
  const r = useBookingRequestPage();
  const [tab, setTab] = useState<TabKey>("overview");
  const [showInvoice, setShowInvoice] = useState(false); // host invoice modal
  const [showUserInvoice, setShowUserInvoice] = useState(false); // guest payment
  const [showReview, setShowReview] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false); // read-only invoice

  if (r.loading) return <BookingDetailsSkeleton />;
  if (!r.booking) {
    return <div className="mx-auto max-w-3xl p-8 text-neutral-600 dark:text-neutral-300">We couldn't load this booking.</div>;
  }

  const { booking: b, status, isHost } = r;
  const shared = b.is_shared_property === true || b.is_shared_property === "true";

  return (
    <PayPalScriptProvider
      options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "", currency: "USD", intent: "capture" }}
    >
      <div className="mx-auto max-w-[1200px] px-4 py-7 sm:px-6 text-neutral-900 dark:text-neutral-100">
        <div className="flex flex-col gap-6">
          <BookingHeader booking={b} status={status} isHost={isHost} />
          <BookingStepper status={status} />
          <BookingTabs active={tab} onChange={setTab} />

          <div className="flex flex-col items-start gap-6 lg:flex-row">
            <div className="w-full min-w-0 flex-1 space-y-6">
              {tab === "overview" && (
                <>
                  {status !== "confirmed" && (
                    <NextStepCard
                      booking={b}
                      status={status}
                      isHost={isHost}
                      isDeleting={r.actions.isDeleting}
                      isCanceling={r.actions.isCanceling}
                      onIssueInvoice={() => setShowInvoice(true)}
                      onViewInvoice={() => setShowUserInvoice(true)}
                      onDeleteInvoice={r.actions.removeInvoice}
                      onCancel={r.actions.cancelBooking}
                      onMessage={() => setTab("messages")}
                    />
                  )}
                  <StayDetails booking={b} status={status} isHost={isHost} checkInDate={r.checkInDate} checkOutDate={r.checkOutDate} />
                  {status === "confirmed" && <PaymentSummary booking={b} isHost={isHost} onViewInvoice={() => setShowInvoiceDetails(true)} />}
                  {status === "confirmed" && !isHost && (
                    <ReviewPrompt
                      booking={b}
                      enabled={r.isReviewEnabled}
                      hasReview={!!b.review?.comment}
                      onOpen={() => setShowReview(true)}
                    />
                  )}
                  <PriceBreakdown booking={b} status={status} isHost={isHost} />
                </>
              )}

              {tab === "messages" && (
                <>
                  <MessageComposer {...r.composer} />
                  <MessageThread
                    modifiedBookingMap={r.chatMessages}
                    loadMoreMessages={r.fetchMessages}
                    showMore={r.loadingMessages}
                    invoiceCard={
                      <InvoiceMessageCard
                        booking={b}
                        status={status}
                        isHost={isHost}
                        onPay={() => setShowUserInvoice(true)}
                      />
                    }
                  />
                </>
              )}
            </div>

            <aside className="w-full shrink-0 space-y-6 lg:w-[340px]">
              <PropertyCard booking={b} status={status} shared={shared} />
              {isHost ? (
                <ProfileCard user={b.user} role="Guest" status={status} onMessage={() => setTab("messages")} />
              ) : (
                <ProfileCard user={b.property_user} role="Host" status={status} onMessage={() => setTab("messages")} />
              )}
            </aside>
          </div>
        </div>

        <InvoiceDetailsDrawer
          opened={showInvoiceDetails}
          onClose={() => setShowInvoiceDetails(false)}
          booking={b}
          status={status}
          isHost={isHost}
        />
        {showInvoice && (
          <Invoice
            bookingRequestDetails={b}
            closeInvoice={() => setShowInvoice(false)}
            callback={r.refresh}
            fetchMessages={r.fetchMessages}
          />
        )}
        {showUserInvoice && <InvoiceUser bookingRequestDetails={b} setShowUserInvoice={setShowUserInvoice} />}
        {showReview && (
          <ReviewForm
            bookingRequestDetails={b}
            isOpen={showReview}
            onClose={() => setShowReview(false)}
            fetchAllManageBooking={r.refresh}
          />
        )}
      </div>
    </PayPalScriptProvider>
  );
};

export default BookingRequestPage;
